import { useEffect, useState } from 'react';
import { ArrowDown, ArrowUp, Lock, Pencil, Trash2 } from 'lucide-react';
import {
  UNIT_COST_ITEM_TAX_TREATMENTS,
  formatAmountInput,
  formatEuro,
  formatPercentInput,
  parseAmountInput,
  parsePercentInput,
  unitCostItemTaxTreatmentLabels,
  type UnitCostItemKind,
  type UnitCostItemRecord,
  type UnitCostItemTaxTreatment,
} from '@brimo/shared';
import {
  Banner,
  Button,
  Checkbox,
  Dialog,
  Icon,
  IconButton,
  Segmented,
  Select,
  TextField,
  useToast,
} from '../../../ds/components';
import { ApiError, errorMessage } from '../../../lib/api';
import { createCostItem, deleteCostItem, orderCostItems, updateCostItem } from '../api';
import { CardHead } from '../bits';
import type { CostItemBody, UnitDetail } from '../types';
import { costAmountLabel, costSum, reorderCostItems } from './unitData';
import styles from './cards.module.css';
import dialogStyles from '../record/dialogs.module.css';

const LISTS: { kind: UnitCostItemKind; title: string; add: string; empty: string }[] = [
  {
    kind: 'incidental_purchase',
    title: 'Kaufnebenkosten',
    add: 'Kaufnebenkosten hinzufügen',
    empty: 'Keine Kaufnebenkosten hinterlegt.',
  },
  {
    kind: 'monthly',
    title: 'Laufende Kosten',
    add: 'Laufende Kosten hinzufügen',
    empty: 'Keine laufenden Kosten hinterlegt.',
  },
];

/** „Kosten" (D-188): „Kaufnebenkosten" and „Laufende Kosten" with add/edit/delete/reorder when editable. */
export function CostItemsCard({
  unit,
  editable,
  onChanged,
}: {
  unit: UnitDetail;
  editable: boolean;
  onChanged: () => void;
}) {
  const { toast } = useToast();
  const [dialog, setDialog] = useState<{ kind: UnitCostItemKind; item: UnitCostItemRecord | null } | null>(
    null,
  );
  const importOnly = unit.external?.mode === 'import_only';
  const ordered = [...unit.costItems].sort((a, b) => a.position - b.position);

  async function move(id: string, step: -1 | 1) {
    try {
      await orderCostItems(unit.id, reorderCostItems(ordered, id, step));
      onChanged();
    } catch (error) {
      toast({ message: errorMessage(error) });
    }
  }

  async function remove(item: UnitCostItemRecord) {
    try {
      await deleteCostItem(unit.id, item.id);
      toast({ message: `${item.label} entfernt.` });
      onChanged();
    } catch (error) {
      toast({ message: errorMessage(error) });
    }
  }

  if (ordered.length === 0 && !editable) return null;

  return (
    <section className={styles.card} aria-labelledby="unit-costs">
      <CardHead eyebrow="Kosten" id="unit-costs" />
      {LISTS.map((list) => {
        const items = ordered.filter((item) => item.kind === list.kind);
        const sum = costSum(items);
        return (
          <div key={list.kind}>
            <div className={styles.listHead}>
              <span className={styles.listTitle}>{list.title}</span>
              {editable ? (
                <Button
                  variant="ghost"
                  size="small"
                  onClick={() => setDialog({ kind: list.kind, item: null })}
                >
                  Hinzufügen
                </Button>
              ) : null}
            </div>
            {items.length === 0 ? (
              <p className={styles.empty}>{list.empty}</p>
            ) : (
              <div className={styles.rows}>
                {items.map((item, index) => {
                  const locked = item.imported && importOnly;
                  return (
                    <div key={item.id} className={styles.row}>
                      <div className={styles.rowMain}>
                        <span className={styles.rowLabel}>{item.label}</span>
                        <span className={styles.rowMeta}>
                          {[item.taxTreatmentLabel, item.includingVat ? 'inkl. MwSt.' : null]
                            .filter(Boolean)
                            .join(' · ')}
                        </span>
                      </div>
                      <span className={styles.rowValue}>{costAmountLabel(item)}</span>
                      {editable ? (
                        <div className={styles.rowActions}>
                          <IconButton
                            icon={ArrowUp}
                            size={28}
                            variant="ghost"
                            label={`${item.label} nach oben`}
                            disabled={index === 0}
                            onClick={() => void move(item.id, -1)}
                          />
                          <IconButton
                            icon={ArrowDown}
                            size={28}
                            variant="ghost"
                            label={`${item.label} nach unten`}
                            disabled={index === items.length - 1}
                            onClick={() => void move(item.id, 1)}
                          />
                          {locked ? (
                            <span className={styles.lock} title="Wird aus TeamProQ übernommen">
                              <Icon icon={Lock} size={16} />
                            </span>
                          ) : (
                            <>
                              <IconButton
                                icon={Pencil}
                                size={28}
                                variant="ghost"
                                label={`${item.label} bearbeiten`}
                                onClick={() => setDialog({ kind: item.kind, item })}
                              />
                              <IconButton
                                icon={Trash2}
                                size={28}
                                variant="ghost"
                                label={`${item.label} entfernen`}
                                onClick={() => void remove(item)}
                              />
                            </>
                          )}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            )}
            {sum !== null ? (
              <div className={styles.sumRow}>
                <span>Summe Beträge</span>
                <span>{list.kind === 'monthly' ? `${formatEuro(sum)} mtl.` : formatEuro(sum)}</span>
              </div>
            ) : null}
          </div>
        );
      })}
      {dialog ? (
        <CostItemDialog
          unitId={unit.id}
          kind={dialog.kind}
          item={dialog.item}
          onClose={() => setDialog(null)}
          onSaved={() => {
            setDialog(null);
            toast({ message: 'Kostenposition gespeichert.' });
            onChanged();
          }}
        />
      ) : null}
    </section>
  );
}

type Basis = 'amount' | 'percent';

function CostItemDialog({
  unitId,
  kind,
  item,
  onClose,
  onSaved,
}: {
  unitId: string;
  kind: UnitCostItemKind;
  item: UnitCostItemRecord | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [label, setLabel] = useState('');
  const [basis, setBasis] = useState<Basis>('amount');
  const [value, setValue] = useState('');
  const [taxTreatment, setTaxTreatment] = useState<UnitCostItemTaxTreatment>('none');
  const [includingVat, setIncludingVat] = useState(false);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    setLabel(item?.label ?? '');
    setBasis(item?.percent !== null && item?.percent !== undefined ? 'percent' : 'amount');
    setValue(
      item?.amountCents !== null && item?.amountCents !== undefined
        ? formatAmountInput(item.amountCents)
        : item?.percent !== null && item?.percent !== undefined
          ? formatPercentInput(item.percent)
          : '',
    );
    setTaxTreatment(item?.taxTreatment ?? 'none');
    setIncludingVat(item?.includingVat ?? false);
    setFields({});
    setError(null);
  }, [item]);

  async function submit() {
    const errors: Record<string, string> = {};
    if (!label.trim()) errors['label'] = 'Bitte eine Bezeichnung angeben.';
    let amountCents: number | null = null;
    let percent: number | null = null;
    if (value.trim() !== '') {
      if (basis === 'amount') {
        amountCents = parseAmountInput(value);
        if (amountCents === null) errors['amountCents'] = 'Bitte einen Betrag angeben.';
      } else {
        percent = parsePercentInput(value);
        if (percent === null) errors['percent'] = 'Bitte einen Prozentsatz angeben.';
      }
    }
    if (Object.keys(errors).length > 0) {
      setFields(errors);
      return;
    }
    const body: CostItemBody = {
      kind,
      label: label.trim(),
      amountCents,
      percent,
      taxTreatment,
      includingVat,
    };
    setPending(true);
    setError(null);
    try {
      if (item) await updateCostItem(unitId, item.id, body);
      else await createCostItem(unitId, body);
      onSaved();
    } catch (failure) {
      if (failure instanceof ApiError && Object.keys(failure.fields).length > 0) setFields(failure.fields);
      else setError(errorMessage(failure));
    } finally {
      setPending(false);
    }
  }

  const listTitle = kind === 'monthly' ? 'Laufende Kosten' : 'Kaufnebenkosten';
  return (
    <Dialog
      open
      onClose={onClose}
      eyebrow={listTitle}
      title={item ? 'Kostenposition bearbeiten' : 'Kostenposition hinzufügen'}
      width={560}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            Abbrechen
          </Button>
          <Button loading={pending} onClick={() => void submit()}>
            Speichern
          </Button>
        </>
      }
    >
      <div className={dialogStyles.form}>
        {error ? <Banner tone="danger">{error}</Banner> : null}
        <TextField
          label="Bezeichnung"
          required
          value={label}
          error={fields['label']}
          hint={kind === 'monthly' ? 'z. B. „Hausgeld“, „Verwaltung“' : 'z. B. „Grunderwerbsteuer“, „Notar“'}
          onChange={(event) => setLabel(event.target.value)}
        />
        <div className={dialogStyles.fieldBlock}>
          <span className={dialogStyles.fieldLabel}>Angabe als</span>
          <Segmented<Basis>
            label="Angabe als"
            value={basis}
            items={[
              { value: 'amount', label: 'Betrag' },
              { value: 'percent', label: 'Prozent vom Kaufpreis' },
            ]}
            onChange={(next) => {
              setBasis(next);
              setValue('');
            }}
          />
        </div>
        <div className={dialogStyles.grid2}>
          {basis === 'amount' ? (
            <TextField
              label={kind === 'monthly' ? 'Betrag mtl.' : 'Betrag'}
              type="money"
              value={value}
              error={fields['amountCents']}
              onChange={(event) => setValue(event.target.value)}
            />
          ) : (
            <TextField
              label="Prozentsatz"
              inputMode="decimal"
              trailing={<span className={dialogStyles.suffix}>%</span>}
              value={value}
              error={fields['percent']}
              onChange={(event) => setValue(event.target.value)}
            />
          )}
          <Select
            label="Steuerliche Behandlung"
            value={taxTreatment}
            options={UNIT_COST_ITEM_TAX_TREATMENTS.map((key) => ({
              value: key,
              label: unitCostItemTaxTreatmentLabels[key],
            }))}
            onChange={(event) => setTaxTreatment(event.target.value as UnitCostItemTaxTreatment)}
          />
        </div>
        <Checkbox
          label="Inkl. MwSt."
          checked={includingVat}
          onChange={(event) => setIncludingVat(event.target.checked)}
        />
      </div>
    </Dialog>
  );
}
